import { ProcessTypes, UserWorkoutSession } from 'fitbeat.gym-core';
import { Gym, UserProfile } from 'fitbeat.models';
import moment from 'moment-timezone';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../../common-types';
import { Button, ButtonTypes, Icon } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { getNextBookingStep as getNextBookingStepAction } from '../actions';
import { BookingFlowFooter } from '../components';
import { currentSelectedGymSelector } from '../selectors';
import { styles as sharedStyles } from './Styles';

interface IProps {
  closeModal: () => void;
  userProfile: UserProfile;
  t: TranslateProp
}

interface IMapStateProps {
  workoutSession: UserWorkoutSession | null;
  checkInLoading: boolean;
  checkInError: string | null;
  gym: Gym | undefined;
}

interface IMapDispatchProps {
  getNextBookingStep(userProfile: UserProfile, processType: ProcessTypes): void;
}

class CheckInStep extends Component<IProps & IMapStateProps & IMapDispatchProps> {
  public render() {
    const { checkInError, checkInLoading } = this.props;
    if (checkInLoading) {
      return this.renderLoading();
    }
    if (checkInError) {
      return this.renderError();
    }
    return this.renderSuccess();
  }

  private renderSuccess = () => {
    const { t, workoutSession } = this.props;
    const startingZoneNumber = UserWorkoutSession.getStartingZoneDisplayNumber(workoutSession);
    return (
      <View style={sharedStyles.container}>
        <View style={styles.bodyContainer}>
          <View style={styles.bodySection}>
            <Icon
              size={defaultScale(80)}
              name='ios-checkmark-circle-outline'
              color={Theme.GREEN}
            />
            <Text style={Theme.textStyle.heading}>
              {t('bookings:checkedIn')}
            </Text>
            <Text style={styles.dateText}>
              {this.getStartingTimeFormatted()}
            </Text>
          </View>
          <View style={styles.divider} />
          {startingZoneNumber && (
            <View style={styles.bodySection}>
              <Text style={styles.startingZoneHeading}>{this.startingInMins()}</Text>
              <View style={styles.startingZoneBox}>
                <Text style={styles.zone}>{t('zone')}</Text>
                <Text style={styles.zoneNumber}>{startingZoneNumber}</Text>
              </View>
            </View>
          )}
        </View>
        <BookingFlowFooter>
          <Button
            buttonType={ButtonTypes.OUTLINE}
            fontColor={Theme.PRIMARY}
            title={t('checkInWorkoutSettingsButton')}
            onPress={this.openBooking}
            containerStyle={styles.topButton}
          />
          <Button
            fontColor={Theme.WHITE}
            title={t('done')}
            onPress={this.done}
          />
        </BookingFlowFooter>
      </View>
    );
  };

  private renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator
        size='small'
        color={Theme.PRIMARY}
        animating
      />
    </View>
  );

  private renderError = () => {
    const { checkInError, t } = this.props;
    return (
      <View style={sharedStyles.container}>
        <View style={styles.container}>
          <Icon
            size={defaultScale(80)}
            name='ios-alert-outline'
            color={Theme.PRIMARY}
          />
          <Text style={styles.title}>{t('checkInErrorHeading')}</Text>
          <Text style={styles.subheading}>
            {checkInError}
          </Text>
        </View>
        <BookingFlowFooter>
          <Button
            fontColor={Theme.WHITE}
            title={t('Done')}
            onPress={this.done}
          />
        </BookingFlowFooter>
      </View>
    );
  };

  private openBooking = async () => {
    const { getNextBookingStep, userProfile } = this.props;
    getNextBookingStep(userProfile, ProcessTypes.Start);
  };

  private done = () => {
    const { closeModal } = this.props;
    closeModal();
  };

  private getStartingTimeFormatted = () => {
    const { workoutSession, gym } = this.props;
    const momentDate = moment.tz(workoutSession?.startTimeOfFirstExercise, gym?.timezone);
    return momentDate.format('HH:mm dddd, Do MMMM');
  };

  private startingInMins = () => {
    const { workoutSession, gym, t } = this.props;
    if (gym && workoutSession) {
      const now = moment().tz(gym.timezone);
      const pendingSlotTime = moment.tz(workoutSession.startTimeOfFirstExercise, gym.timezone);

      const minutesDiff = pendingSlotTime.diff(now.startOf('minute'), 'minutes');
      if (minutesDiff <= 0) {
        return t('checkInScreenWorkoutStarted');
      }
      return t('checkInScreenWorkoutStarting', { minutes: minutesDiff });
    }
    return t('checkInScreenWorkoutStarted');
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...Theme.textStyle.display1Bold,
  },
  subheading: {
    ...Theme.textStyle.headingReg,
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  dateText: {
    ...Theme.textStyle.bodyCopyReg,
    marginTop: defaultScale(10),
  },
  bodyContainer: {
    flex: 1,
  },
  bodySection: {
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  divider: {
    height: defaultScale(2),
    backgroundColor: Theme.GREY_LIGHT,
  },
  startingZoneBox: {
    borderWidth: defaultScale(2),
    borderRadius: defaultScale(10),
    borderColor: Theme.BLACK,
    height: defaultScale(100),
    width: defaultScale(100),
    justifyContent: 'center',
    alignItems: 'center',
  },
  startingZoneHeading: {
    ...Theme.textStyle.bodyCopyBold,
    marginBottom: defaultScale(5),
  },
  zone: {
    ...Theme.textStyle.heading,
  },
  zoneNumber: {
    ...Theme.textStyle.heading,
    fontSize: defaultScale(50),
    lineHeight: defaultScale(50),
  },
  topButton: {
    marginBottom: defaultScale(10),
  },
});

const mapStateToProps = (state: IAppStore): IMapStateProps => ({
  workoutSession: state.bookings.bookingWizard.workoutSession,
  checkInLoading: state.bookings.bookingWizard.checkInLoading,
  checkInError: state.bookings.bookingWizard.checkInError,
  gym: currentSelectedGymSelector(state) || undefined,
});

const mapDispatchToProps = (dispatch): IMapDispatchProps => ({
  getNextBookingStep: (userProfile, processType) => dispatch(getNextBookingStepAction(userProfile, processType)),
});

export default translate(['bookings'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(CheckInStep),
);
